import { createContext, FC, ReactNode, useContext, useState, useCallback } from 'react'
import { Snackbar } from './Snackbar'

interface SnackbarContextType {
	showSnackbar: (message: string, duration?: number) => void
	setContainer: (container: HTMLElement | null) => void
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined)

interface SnackbarProviderProps {
	children: ReactNode
}

export const SnackbarProvider: FC<SnackbarProviderProps> = ({ children }) => {
	const [message, setMessage] = useState<string>('')
	const [isVisible, setIsVisible] = useState(false)
	const [duration, setDuration] = useState(5000)
	const [container, setContainer] = useState<HTMLElement | null>(null)

	const showSnackbar = useCallback((newMessage: string, newDuration = 5000) => {
		setMessage(newMessage)
		setDuration(newDuration)
		setIsVisible(true)
	}, [])

	const handleClose = useCallback(() => {
		setIsVisible(false)
	}, [])

	const handleSetContainer = useCallback((newContainer: HTMLElement | null) => {
		setContainer(newContainer)
	}, [])

	return (
		<SnackbarContext.Provider value={{ showSnackbar, setContainer: handleSetContainer }}>
			{children}
			<Snackbar
				message={message}
				isVisible={isVisible}
				onClose={handleClose}
				duration={duration}
				container={container}
			/>
		</SnackbarContext.Provider>
	)
}

export const useSnackbar = () => {
	const context = useContext(SnackbarContext)
	if (!context) {
		throw new Error('useSnackbar must be used within SnackbarProvider')
	}
	return context
}

